import { MuteText } from '@elements/Common';
import { Flag } from '@elements/Flag';
import { CommonUtility } from '@utility/common';
import { colors } from '@utility/sales-forecast';
import { theme } from '@utility/theme';
import { GlobeSimple } from '@phosphor-icons/react';
import { cn } from '@skupreme/shared/ui';
import { useMemo } from 'react';

const TOOLTIP = cn(
  'sk:min-w-[325px] sk:gap-[15px] sk:rounded-card sk:bg-surface-raised sk:px-2.5 sk:pt-[13px] sk:pb-2.5 sk:text-xs sk:text-fg sk:shadow-card sk:inset-shadow-hairline',
  'sk:[&_table]:w-full sk:[&_td]:px-1.5 sk:[&_td]:py-[3px] sk:[&_td]:text-center sk:[&_th]:px-1.5 sk:[&_th]:py-[3px] sk:[&_th]:text-center',
  'sk:[&_.mute-text]:text-fg-tertiary',
);

/* The rules under the headings repeat the series' line colours and dashes. */
const LEGEND_LINE = 'sk:h-0 sk:w-full sk:border-0 sk:border-b-2';

const getDifferenceData = difference => {
  const className = cn(
    'difference sk:text-fg-tertiary',
    difference > 0 && 'sk:text-success',
    difference < 0 && 'sk:text-danger',
  );
  const value =
    difference !== 0
      ? [
          difference > 0 ? '+' : '',
          CommonUtility.decimalWithCommas(difference, 2),
          '%',
        ].join('')
      : '-';

  return {
    value,
    className,
  };
};

const getSortedPayload = (payload = []) => {
  if (!CommonUtility.isValidArray(payload)) {
    return [];
  }

  // @ts-expect-error - TS2339: Property 'dataKey' does not exist on type 'never'.
  const past = payload.find(d => d.dataKey === 'past');
  // @ts-expect-error - TS2339: Property 'dataKey' does not exist on type 'never'.
  const future = payload.find(d => d.dataKey === 'global');
  // @ts-expect-error - TS2339: Property 'dataKey' does not exist on type 'never'.
  const rest = payload.filter(d => !['past', 'global'].includes(d.dataKey));

  return [past, future, ...rest];
};

function TableHeading({ title = '', description = '' }) {
  return (
    <td>
      <div className="sk:flex sk:flex-col sk:items-start sk:justify-center">
        {title && (
          <MuteText>{description ? <strong>{title}</strong> : title}</MuteText>
        )}
        {description && <MuteText>{description}</MuteText>}
      </div>
    </td>
  );
}

function GlobalFlag() {
  return (
    <GlobeSimple
      className="sk:ml-1 sk:min-h-[15px] sk:min-w-5 sk:rounded-[2px] sk:bg-primary sk:p-px sk:text-surface"
      width={20}
      height={15}
    />
  );
}

/**
 * For Displaying Sales Velocity Information In A Chart Tooltip
 * @param {boolean} props.active - Indicates If The Tooltip Is Active
 * @param {Object[]} props.payload - Array Of Tooltip Payload Data
 * @returns {JSX.Element | null} - React Component Displaying Sales Velocity Information In A Chart Tooltip
 */
function SeasonalityChartTooltip(props) {
  const { active, payload } = props;

  const { differences, pastDate, pastValue, regionPayload } = useMemo(() => {
    if (!active || !CommonUtility.isValidArray(payload)) return {};

    let past = null;
    let future = null;
    const differences = {};

    payload.forEach(d => {
      // @ts-expect-error - TS2339: Property 'dataKey' does not exist on type '{}'.
      if (d?.dataKey === 'global') {
        // @ts-expect-error - TS2322: Type '{}' is not assignable to type 'null'.
        future = d;
        // @ts-expect-error - TS2339: Property 'dataKey' does not exist on type '{}'.
      } else if (d?.dataKey === 'past') {
        // @ts-expect-error - TS2322: Type '{}' is not assignable to type 'null'.
        past = d;
      }
    });

    // @ts-expect-error - TS2339: Property 'payload' does not exist on type 'never'.
    const pastValue = past?.payload?.past || 0;

    payload.forEach(d => {
      // @ts-expect-error - TS2339: Property 'payload' does not exist on type '{}'.
      const value = d?.payload?.[d?.dataKey] || 0;
      // @ts-expect-error - TS2339: Property 'dataKey' does not exist on type '{}'.
      differences[d?.dataKey] = pastValue ? (100 * value) / pastValue - 100 : 0;
    });

    // @ts-expect-error - TS2345: Argument of type 'unknown[]' is not assignable to parameter of type 'never[]'.
    const regionPayload = getSortedPayload(payload).filter(
      // @ts-expect-error - TS2339: Property 'dataKey' does not exist on type 'never'.
      d => d?.dataKey !== 'past' && d?.name,
    );

    return {
      differences,
      // @ts-expect-error - TS2339: Property 'payload' does not exist on type 'never'.
      pastDate: past?.payload?.pastDate || future?.payload?.pastDate,
      pastValue,
      regionPayload,
    };
  }, [active, payload]);

  // If Not Active Or Data Is Not Present → Do Not Render
  if (!active || !CommonUtility.isValidArray(payload)) return null;

  const renderUnits = value => (
    <strong>{value ? CommonUtility.decimalWithCommas(value, 2) : '-'}</strong>
  );

  return (
    <div className={TOOLTIP}>
      <table>
        <thead>
          <tr>
            <th> </th>
            <th>
              <div className="sk:mb-2 sk:flex sk:items-center sk:justify-center">
                <span>Past</span>
              </div>
              <div
                className={cn(LEGEND_LINE, 'sk:border-dashed')}
                style={{ borderColor: theme.colors.grey }}
              />
            </th>
            {
              // @ts-expect-error - ts-migration
              regionPayload.map(d => (
                // @ts-expect-error - ts-migration
                <th key={d?.dataKey}>
                  <div className="sk:mb-2 sk:flex sk:items-center sk:justify-center">
                    {
                      // @ts-expect-error - ts-migration
                      d?.dataKey === 'global' ? (
                        <GlobalFlag />
                      ) : (
                        // @ts-expect-error - TS2339: Property 'name' does not exist on type 'never'.
                        <Flag name={d?.name} />
                      )
                    }
                    {/* @ts-expect-error - ts-migration */}
                    <span className="sk:ml-2">{d?.name}</span>
                  </div>
                  <div
                    className={cn(LEGEND_LINE, 'sk:border-solid')}
                    // @ts-expect-error - TS2339: Property 'stroke' does not exist on type 'never'.
                    style={{ borderColor: d?.stroke || colors.lineGlobal }}
                  />
                </th>
              ))
            }
          </tr>
        </thead>
        <tbody>
          {payload.length > 1 && (
            <tr>
              <TableHeading title="Change" />
              <td />
              {
                // @ts-expect-error - ts-migration
                regionPayload.map(d => {
                  const { className, value } = getDifferenceData(
                    // @ts-expect-error - TS2339: Property 'dataKey' does not exist on type 'never'.
                    differences?.[d?.dataKey] ?? 0,
                  );
                  return (
                    // @ts-expect-error - ts-migration
                    <td key={d.dataKey}>
                      <span className={className}>{value}</span>
                    </td>
                  );
                })
              }
            </tr>
          )}
          <tr>
            <TableHeading title="Date" />
            <td>{pastDate}</td>
            {
              // @ts-expect-error - ts-migration
              regionPayload.map(d => (
                // @ts-expect-error - ts-migration
                <td key={d.dataKey}>{d?.payload?.futureDate}</td>
              ))
            }
          </tr>
          <tr>
            <TableHeading title="Sales Velocity" description="Units / Day" />
            <td>{renderUnits(pastValue)}</td>
            {
              // @ts-expect-error - ts-migration
              regionPayload.map(d => (
                // @ts-expect-error - ts-migration
                <td key={d.dataKey}>
                  {
                    // @ts-expect-error - ts-migration
                    renderUnits(d?.payload?.[d?.dataKey] || 0)
                  }
                </td>
              ))
            }
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default SeasonalityChartTooltip;
